// src/hooks/useFormBuilder.js
import { useState, useEffect, useRef, useCallback, Fragment } from 'react';
import Select from 'react-select';
import { Icon } from '@iconify/react';
import AsyncSelect from 'react-select/async';
import { fetchData } from './apiHelpers';
import { getAll } from '../api';
import { getCache, getOrFetch, subscribeToLookupCache } from './lookupCache';
import { formatDepartmentOption } from './formatDepartmentOption';
import { enhanceHierarchyOptions } from './hierarchy';
import { sortLookupOptions } from './sortLookupOptions';
import { HierarchySelect } from '../components/common/HierarchySelect';
import { useSetting } from './AppSettingsContext';
import UploadProgressModal from '../components/common/UploadProgressModal';
import TimePickerBootstrap from '../components/common/TimePickerBootstrap';
import { showToastError, showToastSuccess } from './errorController';
import DOMPurify from 'dompurify';
import Tooltip from 'bootstrap/js/dist/tooltip';
import {
  HIERARCHY_STRUCTURE_API,
  resolveHierarchyApiName,
} from '../constants/hierarchy';

const ISO_TZ_RE = /(Z|[+-]\d{2}:\d{2})$/;

const toLocalInputFormat = (val, withTime = false) => {
  if (!val) return '';

  // If it's already a correctly-shaped local string, just return it.
  if (typeof val === 'string') {
    // allow partial typing (don't fight the user)
    const localDateOnly = /^\d{4}-\d{2}-\d{2}$/;
    const localDateTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

    if (!ISO_TZ_RE.test(val) && (localDateOnly.test(val) || localDateTime.test(val))) {
      return val;
    }
  }

  // If it has a timezone (Z or +/-hh:mm), convert to local input format.
  // Also handle Date instances from callers.
  let dateObj = null;
  if (val instanceof Date) {
    dateObj = val;
  } else if (typeof val === 'string' && ISO_TZ_RE.test(val)) {
    dateObj = new Date(val);
  } else if (typeof val === 'number') {
    dateObj = new Date(val);
  }

  if (dateObj && !isNaN(dateObj.getTime())) {
    const yyyy = dateObj.getFullYear();
    const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
    const dd = String(dateObj.getDate()).padStart(2, '0');
    if (withTime) {
      const hh = String(dateObj.getHours()).padStart(2, '0');
      const min = String(dateObj.getMinutes()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
    }
    return `${yyyy}-${mm}-${dd}`;
  }

  // Unknown shape: be conservative—don’t transform.
  return typeof val === 'string' ? val : '';
};

const selectStyles = {
  control: (base) => ({
    ...base,
    height: '38px',
    borderRadius: '6px'
  })
};

const INITIAL_UPLOAD_STATE = {
  visible: false,
  progress: 0,
  currentFile: '',
  uploadedBytes: 0,
  totalBytes: 0,
};

const sanitizeCodePart = (value) => {
  if (value === undefined || value === null) return '';
  return String(value).trim();
};

const buildCodeNameLabel = (record, nestedKey) => {
  if (!record || typeof record !== 'object') return '';

  const target = nestedKey && record[nestedKey] && typeof record[nestedKey] === 'object'
    ? record[nestedKey]
    : record;

  const code = sanitizeCodePart(target?.code ?? record?.code);
  const name = sanitizeCodePart(target?.name ?? record?.name);
  const parts = [code, name].filter(Boolean);
  return parts.join(' - ');
};

const getOptionDisplayText = (entry, api, displayField) => {
  let specialLabel = '';

  if (api === 'task-codes') {
    const baseLabel = buildCodeNameLabel(entry, 'taskCode');
    const modifierName = sanitizeCodePart(entry?.modifierCode?.name || "");
    specialLabel = modifierName ? [baseLabel, modifierName].filter(Boolean).join(' - ') : baseLabel;
  } else if (api === 'modifier-codes') {
    specialLabel = buildCodeNameLabel(entry, 'modifierCode');
  }

  if (specialLabel) return specialLabel;

  if (typeof displayField === 'function') {
    return displayField(entry);
  }

  if (displayField && entry && entry[displayField] !== undefined && entry[displayField] !== null) {
    return entry[displayField];
  }

  if (entry && typeof entry.label === 'string') {
    return entry.label;
  }

  return '';
};

const prependNoneOptionIfNeeded = (options, api) => {
  if (api !== 'task-codes' && api !== 'modifier-codes') {
    return options;
  }

  const noneOption = { value: null, label: '(none)' };
  const withoutNull = options.filter((opt) => opt && opt.value !== null);

  return [noneOption, ...withoutNull];
};

const resolveLookupArray = (payload, api) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];

  if (Array.isArray(payload.data)) return payload.data;

  const explicitApiKey = api && Array.isArray(payload[api]) ? payload[api] : null;
  if (explicitApiKey) return explicitApiKey;

  if (api === 'projects' && Array.isArray(payload.projects)) {
    return payload.projects;
  }

  const firstArrayValue = Object.values(payload).find((value) => Array.isArray(value));
  return Array.isArray(firstArrayValue) ? firstArrayValue : [];
};

// Ensure year portion does not exceed 4 characters
const enforceYearLength = (val) => {
  if (typeof val !== 'string' || !val.includes('-')) return val;
  const [year, ...rest] = val.split('-');
  if (year.length > 4) {
    const currentYear = String(new Date().getFullYear());
    return [currentYear, ...rest].join('-');
  }
  return val;
};

const formatDateValue = (val, withTime = false) => {
  if (!val) return '';

  const formatInternal = (v) => {
    if (typeof v === 'string') {
      const clamped = enforceYearLength(v);
      return toLocalInputFormat(clamped, withTime);
    }
    return toLocalInputFormat(v, withTime);
  };

  let formatted = formatInternal(val);

  // If this is datetime-local and the formatted value lacks seconds,
  // append ":00" so step=300 works correctly.
  if (withTime && formatted && formatted.length === 16) {
    formatted = formatted + ':00';
  }

  return formatted;
};

const normalizeTimeValue = (val) => {
  if (!val || typeof val !== 'string') return '';
  const trimmed = val.trim();
  if (!trimmed) return '';
  const raw = trimmed.includes('T') ? trimmed.split('T')[1] : trimmed;
  const match = raw.match(/(\d{1,2})\s*:\s*(\d{1,2})/);
  if (!match) return '';
  const hour = match[1].padStart(2, '0');
  const minute = match[2].padStart(2, '0');
  const hourNumber = Number.parseInt(hour, 10);
  const minuteNumber = Number.parseInt(minute, 10);
  if (Number.isNaN(hourNumber) || Number.isNaN(minuteNumber)) return '';
  if (hourNumber < 0 || hourNumber > 23 || minuteNumber < 0 || minuteNumber > 59) {
    return '';
  }
  return `${hour}:${minute}`;
};

const getDateTimeParts = (val) => {
  const formatted = formatDateValue(val, true);
  if (!formatted || typeof formatted !== 'string') {
    return { date: '', time: '' };
  }
  const [datePart, timePart] = formatted.split('T');
  return {
    date: datePart || '',
    time: normalizeTimeValue(timePart || ''),
  };
};

// Validate and sanitize upload response
const normalizeUploadPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  const { key, url } = payload;
  if (typeof key !== 'string' || key.trim() === '') return null;
  if (/[<>]/.test(key)) return null;
  return { key, url: typeof url === 'string' ? url : '' };
};

const parseUploadResponse = async (res) => {
  try {
    if (res && typeof res.json === 'function') {
      const data = await res.json();
      return normalizeUploadPayload(data);
    }
    return normalizeUploadPayload(res);
  } catch {
    return null;
  }
};

const mergePaginatedOptionLists = (existing = [], incoming = []) => {
  const safeExisting = Array.isArray(existing) ? existing : [];
  const safeIncoming = Array.isArray(incoming) ? incoming : [];

  if (!safeExisting.length) {
    return [...safeIncoming];
  }

  if (!safeIncoming.length) {
    return [...safeExisting];
  }

  const result = [];
  const seen = new Map();

  const pushOption = (option) => {
    if (!option) return;
    const hasValue = option.value !== undefined && option.value !== null;
    const key = hasValue ? `value:${option.value}` : null;

    if (key) {
      if (seen.has(key)) {
        result[seen.get(key)] = option;
        return;
      }
      seen.set(key, result.length);
    }

    result.push(option);
  };

  safeExisting.forEach(pushOption);
  safeIncoming.forEach(pushOption);

  return result;
};

const normalizePaginatedResult = (result, page) => {
  if (Array.isArray(result)) {
    return { options: result, hasMore: false };
  }

  if (!result || typeof result !== 'object') {
    return { options: [], hasMore: false };
  }

  const options = Array.isArray(result.options)
    ? result.options
    : Array.isArray(result.data)
      ? result.data
      : Array.isArray(result.results)
        ? result.results
        : [];

  const pageSize =
    typeof result.pageSize === 'number'
      ? result.pageSize
      : typeof result.page_size === 'number'
        ? result.page_size
        : typeof result.limit === 'number'
          ? result.limit
          : undefined;

  const totalCount =
    typeof result.totalCount === 'number'
      ? result.totalCount
      : typeof result.total === 'number'
        ? result.total
        : typeof result.count === 'number'
          ? result.count
          : undefined;

  let hasMore;
  if (typeof result.hasMore === 'boolean') {
    hasMore = result.hasMore;
  } else if (typeof result.has_more === 'boolean') {
    hasMore = result.has_more;
  } else if (typeof result.nextPage === 'number') {
    hasMore = result.nextPage > page;
  } else if (typeof result.next_page === 'number') {
    hasMore = result.next_page > page;
  } else if (typeof totalCount === 'number' && typeof pageSize === 'number') {
    hasMore = page * pageSize < totalCount;
  } else if (typeof result.remaining === 'number') {
    hasMore = result.remaining > 0;
  } else if (typeof pageSize === 'number') {
    hasMore = Array.isArray(options) && options.length === pageSize;
  } else {
    hasMore = false;
  }

  return {
    options: Array.isArray(options) ? options : [],
    hasMore,
  };
};

const pinCreateOptionsFirst = (options) => {
  if (!Array.isArray(options) || options.length === 0) {
    return [];
  }

  const createOptions = options.filter((option) => option?.isCreate);
  const normalOptions = options.filter((option) => !option?.isCreate);
  return [...createOptions, ...normalOptions];
};

export const useFormBuilder = (inputFields, formData, setFormData, errors, options = {}) => {
  const { refreshKey } = options;
  const [lookupOptions, setLookupOptions] = useState({});
  const [paginatedOptions, setPaginatedOptions] = useState({});
  const [hierarchyTrees, setHierarchyTrees] = useState({});
  const [paginatedLoadingMap, setPaginatedLoadingMap] = useState({});
  const [uploadState, setUploadState] = useState({ ...INITIAL_UPLOAD_STATE });
  const [imageAccessMap, setImageAccessMap] = useState({});
  const [lookupReloadKey, setLookupReloadKey] = useState(0);
  const [tagInputs, setTagInputs] = useState({});
  const maxFileSize = useSetting('max_file_size', 5);
  const shiftStartTimeSetting = useSetting('shift_start_time', '08:00:00');
  const fetchedLookups = useRef(new Set());
  const fetchedPaginated = useRef(new Set());
  const paginatedStateRef = useRef({});
  const uploadControlRef = useRef({ cancel: null, isCancelling: false, keys: [] });
  const uploadModalHostFieldRef = useRef(null);

  const markImageAccessible = useCallback((src, isAccessible) => {
    if (!src) return;
    setImageAccessMap((prev) => {
      if (prev[src] === isAccessible) return prev;
      return { ...prev, [src]: isAccessible };
    });
  }, []);

  const resetUploadControl = useCallback(() => {
    uploadControlRef.current = { cancel: null, isCancelling: false, keys: [] };
  }, []);

  const startUploadSession = useCallback((totalBytes) => {
    resetUploadControl();
    setUploadState({
      visible: true,
      progress: totalBytes > 0 ? 0 : 100,
      currentFile: '',
      uploadedBytes: 0,
      totalBytes,
    });
  }, [resetUploadControl]);

  const updateUploadProgress = useCallback(({ currentFile, uploadedBytes, totalBytes }) => {
    const clampedTotal = totalBytes > 0 ? totalBytes : 0;
    const progress = clampedTotal > 0
      ? Math.min(100, Math.round((uploadedBytes / clampedTotal) * 100))
      : 100;
    setUploadState({
      visible: true,
      currentFile,
      uploadedBytes,
      totalBytes: clampedTotal,
      progress,
    });
  }, []);

  const completeUploadSession = useCallback(() => {
    setUploadState({ ...INITIAL_UPLOAD_STATE });
  }, []);

  const cleanupUploadedKeys = useCallback(async (keys) => {
    if (!Array.isArray(keys) || keys.length === 0) return;
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/upload/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keys }),
        credentials: 'include',
      });
    } catch (err) {
      console.error('Failed to clean up cancelled uploads:', err);
    }
  }, []);

  const handleCancelUpload = useCallback(() => {
    if (uploadControlRef.current.cancel) {
      uploadControlRef.current.isCancelling = true;
      try {
        uploadControlRef.current.cancel();
      } catch (err) {
        console.error('Error cancelling upload:', err);
      }
    }
  }, []);

  const buildAbortError = useCallback((message) => {
    if (typeof DOMException !== 'undefined') {
      return new DOMException(message, 'AbortError');
    }
    const error = new Error(message);
    error.name = 'AbortError';
    return error;
  }, []);

  const uploadFileWithProgress = useCallback((file, directory, onProgress) => {
    return new Promise((resolve, reject) => {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      const token =
        localStorage.getItem('contractor_access_token') ||
        localStorage.getItem('accessToken') ||
        localStorage.getItem('token');

      const xhr = new XMLHttpRequest();
      const timeoutMs = 120000;
      let didTimeout = false;
      const timeoutId = setTimeout(() => {
        didTimeout = true;
        try {
          xhr.abort();
        } catch (err) {
          reject(err instanceof Error ? err : new Error('Upload aborted'));
        }
      }, timeoutMs);

      xhr.open('POST', `${process.env.REACT_APP_API_URL}/upload/${directory}`);
      xhr.withCredentials = true;
      xhr.responseType = 'json';
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && typeof onProgress === 'function') {
          onProgress(event.loaded, event.total || file.size || 0);
        }
      };

      xhr.onload = async () => {
        clearTimeout(timeoutId);
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            let payload = xhr.response;
            if (!payload || typeof payload !== 'object') {
              payload = JSON.parse(xhr.responseText);
            }
            const parsed = await parseUploadResponse(payload);
            if (parsed) {
              uploadControlRef.current.cancel = null;
              uploadControlRef.current.isCancelling = false;
              resolve(parsed);
            } else {
              reject(new Error('Invalid upload response'));
            }
          } catch (err) {
            reject(err instanceof Error ? err : new Error('Unable to parse upload response'));
          }
        } else {
          uploadControlRef.current.cancel = null;
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => {
        clearTimeout(timeoutId);
        uploadControlRef.current.cancel = null;
        reject(new Error('Upload failed due to a network error'));
      };

      xhr.onabort = () => {
        clearTimeout(timeoutId);
        uploadControlRef.current.cancel = null;
        if (didTimeout) {
          const timeoutError = new Error('Upload timed out');
          timeoutError.name = 'UploadTimeoutError';
          reject(timeoutError);
          return;
        }
        if (uploadControlRef.current.isCancelling) {
          reject(buildAbortError('Upload cancelled'));
          return;
        }
        reject(new Error('Upload aborted'));
      };

      uploadControlRef.current.cancel = () => {
        if (xhr.readyState !== 4) {
          uploadControlRef.current.isCancelling = true;
          xhr.abort();
        }
      };

      try {
        xhr.send(formDataUpload);
      } catch (err) {
        clearTimeout(timeoutId);
        reject(err instanceof Error ? err : new Error('Upload request failed'));
      }
    });
  }, [buildAbortError]);

  const runUploadFlow = useCallback(async ({ files, directory, transform }) => {
    const uploadableFiles = Array.isArray(files) ? files.filter(Boolean) : [];
    if (!uploadableFiles.length) {
      return { success: false, results: [] };
    }

    const totalBytes = uploadableFiles.reduce((sum, item) => sum + (item.size || 0), 0);
    startUploadSession(totalBytes);

    const results = [];
    let completedBytes = 0;

    try {
      for (const file of uploadableFiles) {
        updateUploadProgress({
          currentFile: file.name || 'Uploading...',
          uploadedBytes: completedBytes,
          totalBytes,
        });

        const payload = await uploadFileWithProgress(file, directory, (loaded) => {
          updateUploadProgress({
            currentFile: file.name || 'Uploading...',
            uploadedBytes: completedBytes + loaded,
            totalBytes,
          });
        });

        if (!payload) {
          throw new Error('Upload failed');
        }

        uploadControlRef.current.keys.push(payload.key);
        results.push(transform({ file, payload }));

        completedBytes += file.size || 0;

        updateUploadProgress({
          currentFile: file.name || 'Uploading...',
          uploadedBytes: completedBytes,
          totalBytes,
        });
      }

      if (results.length) {
        updateUploadProgress({
          currentFile: '',
          uploadedBytes: totalBytes,
          totalBytes,
        });
        showToastSuccess('Files successfully uploaded');
      }

      return { success: true, results };
    } catch (err) {
      const keysToCleanup = [...uploadControlRef.current.keys];
      if (keysToCleanup.length) {
        await cleanupUploadedKeys(keysToCleanup);
      }

      if (err?.name === 'UploadTimeoutError') {
        showToastError('Upload timed out after 120 seconds.');
      } else if (uploadControlRef.current.isCancelling || err?.name === 'AbortError') {
        showToastError('Upload cancelled.');
      } else {
        showToastError('Upload failed. Please try again.');
      }

      return { success: false, results: [] };
    } finally {
      completeUploadSession();
      resetUploadControl();
    }
  }, [cleanupUploadedKeys, completeUploadSession, resetUploadControl, startUploadSession, updateUploadProgress, uploadFileWithProgress]);

  useEffect(() => {
    fetchedLookups.current = new Set();
    fetchedPaginated.current = new Set();
    setLookupOptions({});
    setPaginatedOptions({});
    setHierarchyTrees({});
    setPaginatedLoadingMap({});
    paginatedStateRef.current = {};
  }, [refreshKey]);

  const fetchPaginatedField = useCallback(
    async (fieldKey, loader, { search, page, optionSortField }) => {
      if (typeof loader !== 'function') {
        paginatedStateRef.current[fieldKey] = {
          term: search,
          page,
          hasMore: false,
        };
        if (page === 1) {
          setPaginatedOptions((prev) => ({ ...prev, [fieldKey]: [] }));
        }
        return [];
      }

      setPaginatedLoadingMap((prev) => ({ ...prev, [fieldKey]: true }));

      try {
        const result = await loader(search, { page, search });
        const { options: fetchedOptions, hasMore } = normalizePaginatedResult(result, page);
        const sortedOptions = pinCreateOptionsFirst(sortLookupOptions(fetchedOptions, {
          optionSortField,
          getLabel: (option) => option?.fullLabel || option?.label || '',
        }));

        setPaginatedOptions((prev) => {
          const prevList = Array.isArray(prev[fieldKey]) ? prev[fieldKey] : [];
          const nextList = page === 1
            ? [...sortedOptions]
            : pinCreateOptionsFirst(sortLookupOptions(mergePaginatedOptionLists(prevList, sortedOptions), {
              optionSortField,
              getLabel: (option) => option?.fullLabel || option?.label || '',
            }));
          return {
            ...prev,
            [fieldKey]: nextList,
          };
        });

        paginatedStateRef.current[fieldKey] = {
          term: search,
          page,
          hasMore: Boolean(hasMore),
        };

        return Array.isArray(sortedOptions) ? sortedOptions : [];
      } catch (err) {
        console.error('Error fetching paginated options for', fieldKey, err);
        paginatedStateRef.current[fieldKey] = {
          term: search,
          page,
          hasMore: false,
        };
        if (page === 1) {
          setPaginatedOptions((prev) => ({ ...prev, [fieldKey]: [] }));
        }
        return [];
      } finally {
        setPaginatedLoadingMap((prev) => ({ ...prev, [fieldKey]: false }));
      }
    },
    [],
  );

  useEffect(() => {
    inputFields
      .filter((f) => f.api)
      .forEach(({ field, api, displayField = 'name', disableLookupFetch, optionSortField }) => {
        if (disableLookupFetch) {
          return;
        }
        if (fetchedLookups.current.has(field)) return;
        fetchedLookups.current.add(field);

        const cached = getCache(api);
        const applyOptions = (arr) => {
          const sorted = sortLookupOptions(arr, {
            optionSortField,
            getLabel: (item) => getOptionDisplayText(item, api, displayField),
          });

          setLookupOptions(prev => {
            if (prev[field]) return prev;

            let options = sorted.map(d => {
              const displayLabel = getOptionDisplayText(d, api, displayField);
              const opt = {
                value: d.id,
                label: displayLabel ?? '',
                raw: d,
              };
              if (api === HIERARCHY_STRUCTURE_API) {
                opt.parentId = d.parentId;
                const parent = sorted.find(p => p.id === d.parentId);
                opt.parentName = parent ? parent.name : '';
              }
              if (d.colours?.bootstrapName) {
                opt.colour = d.colours.bootstrapName;
              }
              return opt;
            });

            if (api === HIERARCHY_STRUCTURE_API) {
              const { options: enhanced, tree } = enhanceHierarchyOptions(options);
              options = enhanced;
              setHierarchyTrees(prev => ({ ...prev, [field]: tree }));
            }

            options = prependNoneOptionIfNeeded(options, api);

            return {
              ...prev,
              [field]: options,
            };
          });
        };

        if (cached) {
          const arr = resolveLookupArray(cached, api);
          applyOptions(arr);
        } else {
          const fetchApiName = resolveHierarchyApiName(api);
          getOrFetch(api, () => fetchData(getAll, { apiName: fetchApiName }))
            .then((data) => {
              const arr = resolveLookupArray(data, api);
              applyOptions(arr);
            })
            .catch((err) => {
              console.error('Error fetching options for', api, err);
            });
        }
      });
  }, [inputFields, refreshKey, lookupReloadKey]);

  useEffect(() => {
    const unsubscribe = subscribeToLookupCache((api) => {
      const relevantFields = inputFields.filter((fieldConfig) => {
        if (!fieldConfig.api) return false;
        if (fieldConfig.disableLookupFetch) return false;
        return api === null || fieldConfig.api === api;
      });

      if (!relevantFields.length) {
        return;
      }

      const fields = relevantFields.map(({ field }) => field);

      fields.forEach((field) => {
        fetchedLookups.current.delete(field);
      });

      setLookupOptions((prev) => {
        if (!prev) return prev;
        let changed = false;
        const next = { ...prev };
        fields.forEach((field) => {
          if (field in next) {
            delete next[field];
            changed = true;
          }
        });
        return changed ? next : prev;
      });

      setHierarchyTrees((prev) => {
        if (!prev) return prev;
        let changed = false;
        const next = { ...prev };
        fields.forEach((field) => {
          if (field in next) {
            delete next[field];
            changed = true;
          }
        });
        return changed ? next : prev;
      });

      setLookupReloadKey((prev) => prev + 1);
    });

    return unsubscribe;
  }, [inputFields]);

  useEffect(() => {
    inputFields.forEach(({ field, type, loadOptions, skipInitialFetch, optionSortField }) => {
      if (type !== 'paginated-select' || typeof loadOptions !== 'function') return;
      if (fetchedPaginated.current.has(field)) return;
      if (skipInitialFetch) return;
      fetchedPaginated.current.add(field);
      (async () => {
        await fetchPaginatedField(field, loadOptions, {
          search: '',
          page: 1,
          optionSortField,
        });
      })();
    });
  }, [inputFields, refreshKey, fetchPaginatedField]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    const newValue =
      type === 'date' || type === 'datetime-local' ? enforceYearLength(value) : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
  };

  const updateTagInput = (field, nextValue) => {
    setTagInputs((prev) => ({ ...prev, [field]: nextValue }));
  };

  const addTagValue = (field, tagValue) => {
    const trimmed = String(tagValue || '').trim();
    if (!trimmed) return;
    const currentTags = Array.isArray(formData[field]) ? formData[field] : [];
    const exists = currentTags.some(
      (tag) => String(tag).trim().toLowerCase() === trimmed.toLowerCase(),
    );
    if (exists) {
      updateTagInput(field, '');
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [field]: [...currentTags, trimmed],
    }));
    updateTagInput(field, '');
  };

  const removeTagValue = (field, tagIndex) => {
    const currentTags = Array.isArray(formData[field]) ? formData[field] : [];
    if (tagIndex < 0 || tagIndex >= currentTags.length) return;
    setFormData((prev) => ({
      ...prev,
      [field]: currentTags.filter((_, idx) => idx !== tagIndex),
    }));
  };

  useEffect(() => {
    const tooltipButtons = document.querySelectorAll('.tooltip-infoIcon');

    // Initialize a tooltip for each button
    const tooltipInstances = Array.from(tooltipButtons).map((tooltipButton) => {
      const tooltipContent = tooltipButton.nextElementSibling.innerHTML;

      return new Tooltip(tooltipButton, {
        title: tooltipContent,
        trigger: 'hover',
        html: true,
        customClass: tooltipButton.getAttribute('data-bs-custom-class') || '',
      });
    });

    // Cleanup tooltips when the component unmounts
    return () => {
      tooltipInstances.forEach((tooltip) => tooltip.dispose());
    };
  }, []);

  const renderField = ({
    field,
    label,
    hideLabel = false,
    type,
    size = '6',
    lines = '3',
    options = [],
    optionsFilter,
    required = false,
    directory = '',
    disabled = '',
    loadOptions: loadOptionsProp,
    selectedOption,
    setSelectedOption,
    onImageClick,
    formatOptionLabel,
    api,
    displayField,
    optionSortField,
    html,
    maxFiles,
    showImage = true,
    tooltip = null,
    mustBeGreaterThanZero,
    mustBePositiveInteger,
    mustBePositiveDecimal,
    maxDecimalPlaces,
    allowZeroValue,
    mustBeAfterField,
    mustBeAfterFieldMessage,
    noFutureDate,
    condition,
    ...rest
  }) => {
    const rawValue = formData[field];
    const value = type === 'number' && typeof rawValue === 'number' && !Number.isFinite(rawValue) ? '' : rawValue;
    const baseOptionsList = options.length ? options : lookupOptions[field] || [];
    const optionsList =
      typeof optionsFilter === 'function' ? baseOptionsList.filter(optionsFilter) : baseOptionsList;

    if (type === 'spacer') {
      return <div className={`col-md-${size}`} key={field}></div>;
    }

    const isUploadFieldType = type === 'image' || type === 'images' || type === 'files';
    if (isUploadFieldType && !uploadModalHostFieldRef.current) {
      uploadModalHostFieldRef.current = field;
    }
    const shouldRenderUploadModal = isUploadFieldType && uploadModalHostFieldRef.current === field;

    // Hide label if attribute set
    let hidden = '';
    hideLabel ? hidden = 'd-none' : hidden = '';

    return (
      <Fragment key={field}>
        {shouldRenderUploadModal && (
          <UploadProgressModal
            show={uploadState.visible}
            progress={uploadState.progress}
            currentFile={uploadState.currentFile}
            uploadedBytes={uploadState.uploadedBytes}
            totalBytes={uploadState.totalBytes}
            onCancel={handleCancelUpload}
          />
        )}
        <div className={`col-md-${size}`}>
          <div className="d-flex align-items-center">
            <label className={`form-label mb-8 ${hidden}`}>
              {label}
              {required && <span className="text-danger mx-4">*</span>}
            </label>
            {tooltip && (
              <span>
                <button
                  type="button"
                  className="tooltip-infoIcon inline-grid text-primary-600"
                  data-bs-toggle="tooltip"
                  data-bs-custom-class="tooltip-primary"
                  data-bs-placement="right"
                >
                  <Icon
                    icon="mage:information-circle-fill"
                    className="text-primary text-lg mt-0"
                    style={{ position: "relative", top: "-5px" }}
                  />{" "}
                </button>
                <div className="my-tooltip tip-content hidden text-start shadow">
                  <span className="text-white">{tooltip}</span>
                </div>
              </span>
            )}
          </div>
          {type === 'text' || type === 'number' ? (
            <input
              type={type}
              name={field}
              min={type === 'number' ? '0' : undefined}
              step={type === 'number' ? '0.01' : undefined}
              className={`form-control ${errors[field] ? 'is-invalid' : ''}`}
              value={value ?? ''}
              onChange={handleChange}
              disabled={disabled}
              {...rest}
            />
          ) : type === 'textarea' ? (
            <textarea
              name={field}
              className={`form-control ${errors[field] ? 'is-invalid' : ''}`}
              value={value ?? ''}
              rows={lines}
              onChange={handleChange}
              disabled={disabled}
              {...rest}
            />
          ) : type === 'tags' ? (
            <>
              <input
                type="text"
                name={field}
                className={`form-control ${errors[field] ? 'is-invalid' : ''}`}
                value={tagInputs[field] ?? ''}
                onChange={(e) => updateTagInput(field, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTagValue(field, tagInputs[field]);
                  }
                }}
                placeholder={rest.placeholder || 'Type a tag and press Enter'}
                disabled={disabled}
              />
              {Array.isArray(value) && value.length > 0 && (
                <div className="mt-10 d-flex flex-wrap gap-2">
                  {value.map((tag, idx) => (
                    <span
                      key={`${field}-tag-${idx}`}
                      className="badge bg-secondary-focus text-secondary text-md fw-normal d-inline-flex align-items-center gap-2 rounded-pill"
                    >
                      <span>{tag.toLowerCase()}</span>
                      {!disabled && (
                        <button
                          type="button"
                          className="btn btn-link p-0 text-secondary text-md d-inline-flex align-items-center"
                          aria-label={`Remove ${tag}`}
                          onClick={() => removeTagValue(field, idx)}
                          style={{ lineHeight: 1 }}
                        >
                          <Icon icon="mdi:close" className="icon" />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </>
          ) : type === 'select' ? (
            (() => {
              const { onChange: customOnChange, isMulti: restIsMulti, ...restProps } = rest;
              const getSelectedOption = () => {
                if (selectedOption) {
                  return selectedOption;
                }

                if (value === undefined || value === null) {
                  return null;
                }

                return (
                  optionsList.find((opt) => {
                    if (!opt) return false;
                    if (opt.value === value) return true;
                    if (opt.value === undefined || opt.value === null) {
                      return value === opt.value;
                    }
                    return String(opt.value) === String(value);
                  }) ?? null
                );
              };

              if (api === HIERARCHY_STRUCTURE_API) {
                const resolvedValue =
                  value === undefined || value === null || value === '' ? null : value;

                return (
                  <HierarchySelect
                    name={field}
                    styles={selectStyles}
                    classNamePrefix={`react-select ${errors[field] ? 'is-invalid' : ''}`}
                    options={optionsList}
                    tree={hierarchyTrees[field]}
                    value={resolvedValue}
                    isMulti={Boolean(restIsMulti)}
                    onChange={(next) => {
                      if (customOnChange) {
                        customOnChange(next, setFormData, field);
                      } else {
                        setFormData(plan => ({ ...plan, [field]: next ?? null }));
                      }
                    }}
                    placeholder={`Select ${label}`}
                    isClearable={restProps.isClearable ?? false}
                    isDisabled={Boolean(disabled)}
                    {...restProps}
                  />
                );
              }

              return (
                <Select
                  name={field}
                  styles={selectStyles}
                  classNamePrefix={`react-select ${errors[field] ? 'is-invalid' : ''}`}
                  value={getSelectedOption()}
                  onChange={(opt) => {
                    if (setSelectedOption) {
                      setSelectedOption(opt ?? null);
                    }

                    if (customOnChange) {
                      customOnChange(opt, setFormData, field);
                    } else {
                      setFormData(plan => ({ ...plan, [field]: opt?.value ?? null }));
                    }
                  }}
                  options={optionsList}
                  isClearable={restProps.isClearable ?? false}
                  placeholder={`Select ${label}`}
                  formatOptionLabel={
                    formatOptionLabel ||
                    (api === HIERARCHY_STRUCTURE_API ? formatDepartmentOption : undefined)
                  }
                  isDisabled={Boolean(disabled)}
                  {...restProps}
                />
              );
            })()
          ) : type === 'multi-select' ? (
            (() => {
              const { onChange: customOnChange, isMulti: restIsMulti, ...restProps } = rest;
              if (api === HIERARCHY_STRUCTURE_API) {
                const multiValue = Array.isArray(value) ? value : [];

                return (
                  <HierarchySelect
                    name={field}
                    styles={selectStyles}
                    classNamePrefix={`react-select ${errors[field] ? 'is-invalid' : ''}`}
                    options={optionsList}
                    tree={hierarchyTrees[field]}
                    value={multiValue}
                    isMulti={restIsMulti ?? true}
                    onChange={(next) => {
                      const values = Array.isArray(next) ? next : [];
                      if (customOnChange) {
                        customOnChange(values, setFormData, field);
                      } else {
                        setFormData(plan => ({ ...plan, [field]: values }));
                      }
                    }}
                    placeholder={`Select ${label}`}
                    isClearable={restProps.isClearable ?? false}
                    isDisabled={Boolean(disabled)}
                    {...restProps}
                  />
                );
              }

              return (
                <Select
                  name={field}
                  styles={selectStyles}
                  isMulti
                  classNamePrefix={`react-select ${errors[field] ? 'is-invalid' : ''}`}
                  value={Array.isArray(value) ? optionsList.filter(opt => value.includes(opt.value)) : []}
                  onChange={(selected) => {
                    const values = (selected || []).map(s => s.value ?? null);
                    if (customOnChange) {
                      customOnChange(values, setFormData, field);
                    } else {
                      setFormData(plan => ({ ...plan, [field]: values }));
                    }
                  }}
                  options={optionsList}
                  placeholder={`Select ${label}`}
                  formatOptionLabel={
                    formatOptionLabel ||
                    (api === HIERARCHY_STRUCTURE_API ? formatDepartmentOption : undefined)
                  }
                  isDisabled={Boolean(disabled)}
                  {...restProps}
                />
              );
            })()
          ) : type === 'paginated-select' ? (
            (() => {
              const {
                onChange: customOnChange,
                minSearchLength: minSearchLengthProp,
                 key: fieldKey,
                ...restProps
              } = rest;
              const fieldLoadOptions = loadOptionsProp;
              const minSearchLength = typeof minSearchLengthProp === 'number' ? minSearchLengthProp : 2;

              const paginatedFieldOptions = Array.isArray(paginatedOptions[field]) ? paginatedOptions[field] : [];

              const loadAsyncOptions = async (inputValue) => {
                const raw = typeof inputValue === 'string' ? inputValue : '';
                const trimmed = raw.trim();
                const shouldLoadDefault = trimmed === '';

                if (!shouldLoadDefault && trimmed.length < minSearchLength) {
                  paginatedStateRef.current[field] = {
                    term: trimmed,
                    page: 1,
                    hasMore: false,
                  };
                  setPaginatedOptions((prev) => {
                    const current = prev[field];
                    if (!current || current.length === 0) return prev;
                    return { ...prev, [field]: [] };
                  });
                  return [];
                }

                const searchTerm = shouldLoadDefault ? '' : trimmed;
                const currentState = paginatedStateRef.current[field] || {};
                if (currentState.term === searchTerm && currentState.page >= 1 && paginatedFieldOptions.length > 0) {
                  return paginatedFieldOptions;
                }

                return fetchPaginatedField(field, fieldLoadOptions, {
                  search: searchTerm,
                  page: 1,
                  optionSortField,
                });
              };

              const handleMenuScrollToBottom = async () => {
                if (!fieldLoadOptions) return;
                const state = paginatedStateRef.current[field] || {};
                const isLoading = Boolean(paginatedLoadingMap[field]);
                if (isLoading || !state.hasMore) {
                  return;
                }

                const searchTerm = typeof state.term === 'string' ? state.term : '';
                const trimmed = searchTerm.trim();
                if (trimmed && trimmed.length < minSearchLength) {
                  return;
                }

                const nextPage = (state.page || 1) + 1;
                await fetchPaginatedField(field, fieldLoadOptions, {
                  search: searchTerm,
                  page: nextPage,
                  optionSortField,
                });
                await loadAsyncOptions(searchTerm);
              };

              return (
                <AsyncSelect
                  classNamePrefix={`react-select ${errors[field] ? 'is-invalid' : ''}`}
                  styles={selectStyles}
                  loadOptions={loadAsyncOptions}
                  cacheOptions
                  defaultOptions={
                    paginatedFieldOptions.length
                      ? paginatedFieldOptions
                      : selectedOption
                        ? [selectedOption]
                        : []
                  }
                  value={selectedOption || null}
                  onChange={(opt) => {
                    if (customOnChange) {
                      customOnChange(opt, setFormData, field, setSelectedOption);
                    } else {
                      if (setSelectedOption) setSelectedOption(opt ?? null);
                      setFormData((prev) => ({ ...prev, [field]: opt?.value ?? null }));
                    }

                    if (!opt && fieldLoadOptions) {
                      fetchPaginatedField(field, fieldLoadOptions, {
                        search: '',
                        page: 1,
                        optionSortField,
                      });
                    }
                  }}
                  formatOptionLabel={
                    formatOptionLabel ||
                    (api === HIERARCHY_STRUCTURE_API ? formatDepartmentOption : undefined)
                  }
                  isClearable={restProps.isClearable ?? true}
                  placeholder={`Start typing to search ...`}
                  isDisabled={Boolean(disabled)}
                  onMenuScrollToBottom={handleMenuScrollToBottom}
                  isLoading={Boolean(paginatedLoadingMap[field])}
                  {...restProps}
                />
              );
            })()
          ) : type === 'radio' ? (
            <div className="d-flex gap-4 mt-10 mb-12">
              {optionsList.map((opt) => (
                <div className="form-check d-flex align-items-center" key={opt.value}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name={field}
                    id={`${field}-${opt.value}`}
                    checked={value === opt.value}
                    onChange={() =>
                      setFormData((prev) => ({
                        ...prev,
                        [field]: opt.value,
                      }))
                    }
                    disabled={disabled}
                  />
                  <label
                    className="form-check-label ms-2"
                    htmlFor={`${field}-${opt.value}`}
                  >
                    {opt.label}
                  </label>
                </div>
              ))}
            </div>

          ) : type === 'switch' ? (
            (() => {
              const { switchColor = 'primary', onChange: customOnChange, ...restProps } = rest;
              return (
                <div className={`form-switch switch-${switchColor} d-flex align-items-center gap-3 mt-10 mb-10`}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    name={field}
                    checked={Boolean(value)}
                    onChange={(e) => {
                      const nextValue = e.target.checked ? 1 : 0;
                      if (customOnChange) {
                        customOnChange(nextValue, setFormData, field);
                      } else {
                        setFormData((prev) => ({
                          ...prev,
                          [field]: nextValue,
                        }));
                      }
                    }}
                    disabled={disabled}
                    {...restProps}
                  />
                  <label className="form-check-label line-height-1 fw-medium text-secondary-light">
                    {value ? 'Yes' : 'No'}
                  </label>
                </div>
              );
            })()
          ) : type === 'date' ? (
            <input
              type="date"
              name={field}
              className={`form-control ${errors[field] ? 'is-invalid' : ''}`}
              value={formatDateValue(value)}
              onChange={handleChange}
              {...rest}
            />
          ) : type === 'time' ? (
            <input
              type="time"
              name={field}
              className={`form-control ${errors[field] ? 'is-invalid' : ''}`}
              value={typeof value === 'string' ? value : ''}
              onChange={handleChange}
              step={300} // 5 minutes = 300 seconds
              {...rest}
            />
          ) : type === 'datetime' ? (
            (() => {
              const { date, time } = getDateTimeParts(value);
              const defaultTime = normalizeTimeValue(shiftStartTimeSetting);
              const resolvedTime = time || defaultTime || null;

              return (
                <div className="d-flex flex-column flex-md-row gap-8">
                  <input
                    type="date"
                    name={`${field}-date`}
                    className={`form-control ${errors[field] ? 'is-invalid' : ''}`}
                    value={date}
                    onChange={(e) => {
                      const nextDate = enforceYearLength(e.target.value);
                      if (!nextDate) {
                        setFormData((prev) => ({ ...prev, [field]: '' }));
                        return;
                      }
                      setFormData((prev) => ({
                        ...prev,
                        [field]: resolvedTime ? `${nextDate}T${resolvedTime}` : nextDate,
                      }));
                    }}
                    disabled={disabled}
                    {...rest}
                  />
                  <TimePickerBootstrap
                    value={resolvedTime}
                    ariaLabel={`${label || field} time`}
                    onChange={(nextTime) => {
                      if (!nextTime) {
                        setFormData((prev) => ({ ...prev, [field]: '' }));
                        return;
                      }
                      if (!date) {
                        setFormData((prev) => ({ ...prev, [field]: '' }));
                        return;
                      }
                      setFormData((prev) => ({
                        ...prev,
                        [field]: `${date}T${nextTime}`,
                      }));
                    }}
                    disabled={disabled}
                    className={errors[field] ? 'is-invalid' : ''}
                  />
                </div>
              );
            })()
          ) : type === 'html' ? (
            <div
              className='mt-16'
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html || '') }}
            />
          ) : type === 'image' ? (
            <>
              <input
                type="file"
                id={`file-${field}`}
                name={field}
                accept=".png, .jpg, .jpeg"
                style={{ display: 'none' }}
                className={`form-control ${errors[field] ? 'is-invalid' : ''}`}
                onChange={async (e) => {
                  const file = e.target.files[0];
                  e.target.value = '';
                  if (!file) return;

                  const { success, results } = await runUploadFlow({
                    files: [file],
                    directory: directory || 'modelimages',
                    transform: ({ payload }) => payload.key,
                  });

                  if (success && results[0]) {
                    setFormData((prev) => ({
                      ...prev,
                      [field]: results[0],
                    }));
                  }
                }}
              />
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => document.getElementById(`file-${field}`).click()}
                disabled={disabled}
              >
                <Icon icon="mage:image" className="icon text-xl line-height-1 me-4" />
                Add Image
              </button>
              {(() => {
                const imageSrc = value ? `${process.env.REACT_APP_SPACES_BASE}/${value}` : '';
                const isAccessible = imageSrc ? imageAccessMap[imageSrc] !== false : false;
                return value && showImage && isAccessible ? (
                <div className="mt-16">
                  <img
                    src={imageSrc}
                    alt="Uploaded"
                    onLoad={() => markImageAccessible(imageSrc, true)}
                    onError={() => markImageAccessible(imageSrc, false)}
                    style={{
                      // width: '105px',
                      maxHeight: '155px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                    }}
                  />
                </div>
                ) : null;
              })()}
            </>

          ) : type === 'images' ? (
            <>
              <input
                type="file"
                id={`file-${field}`}
                name={field}
                accept=".png, .jpg, .jpeg"
                multiple
                style={{ display: 'none' }}
                className={`form-control ${errors[field] ? 'is-invalid' : ''}`}
                disabled={
                  disabled || (maxFiles && Array.isArray(value) && value.length >= maxFiles)
                }
                onChange={async (e) => {
                  const selectedFiles = Array.from(e.target.files);
                  if (!selectedFiles.length) return;

                  const currentCount = Array.isArray(formData[field])
                    ? formData[field].length
                    : 0;
                  const remainingSlots =
                    typeof maxFiles === 'number' ? maxFiles - currentCount : Infinity;

                  if (remainingSlots <= 0) {
                    alert(`You can upload up to ${maxFiles} images.`);
                    e.target.value = '';
                    return;
                  }

                  const files = selectedFiles.slice(0, remainingSlots);
                  if (files.length < selectedFiles.length) {
                    alert(`You can upload up to ${maxFiles} images.`);
                  }

                  if (!files.length) {
                    e.target.value = '';
                    return;
                  }

                  const { success, results } = await runUploadFlow({
                    files,
                    directory: directory || 'modelimages',
                    transform: ({ payload }) => payload.key,
                  });

                  if (success && results.length) {
                    setFormData((prev) => ({
                      ...prev,
                      [field]: [...(prev[field] || []), ...results],
                    }));
                  }

                  e.target.value = '';
                }}
              />
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => document.getElementById(`file-${field}`).click()}
                disabled={disabled}
              >
                <Icon icon="mage:image" className="icon text-xl line-height-1 me-4" />
                Add photos
              </button>
              {Array.isArray(value) && value.length > 0 && (
                <div className="mt-16 d-flex flex-wrap gap-2">
                  {value.map((img, idx) => (
                    <div
                      key={idx}
                      style={{
                        position: 'relative',
                        width: '105px',
                        height: '105px',
                      }}
                    >
                      {(() => {
                        const imageSrc = `${process.env.REACT_APP_SPACES_BASE}/${img}`;
                        if (imageAccessMap[imageSrc] === false) {
                          return null;
                        }
                        return (
                          <img
                            src={imageSrc}
                            alt="Uploaded"
                            onLoad={() => markImageAccessible(imageSrc, true)}
                            onError={() => markImageAccessible(imageSrc, false)}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: '4px',
                              border: '1px solid #ccc',
                              cursor: onImageClick ? 'pointer' : 'default'
                            }}
                            onClick={() =>
                              onImageClick &&
                              onImageClick(
                                `${process.env.REACT_APP_SPACES_UPLOAD}/${img}`,
                              )
                            }
                          />
                        );
                      })()}
                      {!disabled && (
                        <button
                          type="button"
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              [field]: prev[field].filter((_, i) => i !== idx),
                            }))
                          }
                          style={{
                            position: 'absolute',
                            top: '-6px',
                            right: '-6px',
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            border: 'none',
                            background: '#dc3545',
                            color: '#fff',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0,
                            lineHeight: 1,
                          }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>

          ) : type === 'files' ? (
            <>
              <input
                type="file"
                name={field}
                accept={
                  rest.accept ||
                  '.jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.bmp,.gif,.heic,.dwg,.dxf,.svg'
                }
                multiple
                className={`form-control ${errors[field] ? 'is-invalid' : ''}`}
                disabled={Boolean(disabled)}
                onChange={async (e) => {
                  if (disabled) {
                    e.target.value = '';
                    return;
                  }
                  const files = Array.from(e.target.files);
                  e.target.value = '';
                  if (!files.length) return;

                  const validFiles = [];
                  for (const file of files) {
                    if (file.size > maxFileSize * 1024 * 1024) {
                      showToastError(`File "${file.name}" exceeds ${maxFileSize}MB limit and was not uploaded.`);
                      continue;
                    }
                    validFiles.push(file);
                  }

                  if (!validFiles.length) {
                    return;
                  }

                  const { success, results } = await runUploadFlow({
                    files: validFiles,
                    directory: directory || 'assetfiles',
                    transform: ({ file: original, payload }) => ({
                      name: original.name,
                      key: payload.key,
                      filename: payload.key,
                      size: original.size,
                    }),
                  });

                  if (success && results.length) {
                    setFormData((prev) => ({
                      ...prev,
                      [field]: [...(prev[field] || []), ...results],
                    }));
                  }
                }}
              />
            </>

          ) : type === 'emptyBorder' ? (
            <div
              id={field}
              style={{
                height: '328px',
                borderRadius: '6px',
                borderColor: '#d1d5db',
                borderWidth: '1px',
                width: '47.5%',
                position: 'absolute',
              }}></div>
          ) : type === 'emptyBorderSm' ? (
            <div
              id={field}
              style={{
                height: '112px',
                borderRadius: '6px',
                borderColor: '#d1d5db',
                borderWidth: '1px',
                width: '358px',
                position: 'absolute',
              }}></div>
          ) : type === 'empty' ? (
            <div style={{ height: '40px' }}></div>
          ) :

            null}

          {errors[field] && (
            <div className={type.includes('select') ? 'react-select-invalid' : 'invalid-feedback d-block'}>
              {errors[field]}
            </div>
          )}
        </div>
      </Fragment>
    );
  };


  return { renderField };
};
